import React, { FunctionComponent, useEffect, useState } from 'react';
import { Spinner, Button, ConfirmDialog } from 'components/UI';
import { CardGroup } from 'components/ClientCards/Card/CardGroup';
import AddCreditCard from 'components/ClientCards/Card/AddCreditCard';
import Header from 'components/ClientCards/Header';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import ButtonV3 from 'components/UI/V3/Button/Button';
import { ClientEditHeader } from 'components/Client/ClientEditHeader';
import { showError, showSuccess } from '@spike/notifications-action';
import { useDispatch, useSelector } from 'react-redux';
import {
    deleteClientPaymentMethodThunk,
    getClientPaymentMethodsThunk,
    PaymentsStatus,
    setPrimaryClientPaymentMethodThunk
} from '@spike/payments-action';
import { fetchWalletThunk, saveWalletAdjustmentThunk, WalletsStatus } from '@spike/wallets-action';
import { Wallet, WalletAdjustmentType } from '@spike/wallet-model';
import { useApiClientWrapper, useRoles } from 'hooks';
import { RootState } from 'store';
import { ClientPaymentMethod } from '@spike/payments-model';
import useNonInitialEffect from '@versiondos/hooks';
import Client from '@spike/client-model';
import { useMediaQuery } from 'react-responsive';
import { Typography } from '@material-ui/core';

import WalletTable from './Table/WalletTable';
import WalletAdjustmentModal from './WalletAdjustmentModal';
import styles from './ClientWallet.module.scss';

export interface ClientWalletProps {
    clientId: number;
    clientRef: Client;
    onBook: () => void;
    onNewSale?: () => void;
    onClose: () => void;
    onSelect: (key: string) => void;
}

const ClientWallet: FunctionComponent<ClientWalletProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { isOwner } = useRoles();
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const [loading, setLoading] = useState(true);
    const [showAddCard, setShowAddCard] = useState(false);
    const [showDeleteCard, setShowDeleteCard] = useState(false);
    const [cardSelectedToDelete, setCardSelectedToDelete] = useState<ClientPaymentMethod | null>(null);
    const [showAddBalance, setShowAddBalance] = useState(false);
    const [showReduceBalance, setShowReduceBalance] = useState(false);

    const unorderedCards = useSelector<RootState, ClientPaymentMethod[]>(state => state.payments.clientPaymentMethods);
    const cards = [...unorderedCards].sort((a, b) => (a.primary === b.primary ? 0 : a.primary ? -1 : 1));
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const wallet = useSelector<RootState, Wallet | undefined>(state => state.wallets.wallet);
    const walletsStatus = useSelector<RootState, WalletsStatus>(state => state.wallets.status);
    const isSaving = walletsStatus === WalletsStatus.Saving;

    const balance = wallet?.balance ?? 0;
    const transactions = wallet?.transactions ?? [];

    useEffect(() => {
        dispatch(getClientPaymentMethodsThunk(apiClientWrapper, props.clientId));
        dispatch(fetchWalletThunk(apiClientWrapper, props.clientId));
    }, [props.clientId]);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.GettingClientPaymentMethods:
                setLoading(true);
                break;
            case PaymentsStatus.GetClientPaymentMethodsSuccess:
                setLoading(false);
                break;
            case PaymentsStatus.DeleteClientPaymentMethodSuccess:
                dispatch(getClientPaymentMethodsThunk(apiClientWrapper, props.clientId));
                setLoading(false);
                break;
            case PaymentsStatus.SettingPrimaryClientPaymentMethod:
                setLoading(true);
                break;
            case PaymentsStatus.SetPrimaryClientPaymentMethodSuccess:
                dispatch(getClientPaymentMethodsThunk(apiClientWrapper, props.clientId));
                break;
            case PaymentsStatus.SetPrimaryClientPaymentMethodError:
                setLoading(false);
                break;
        }
    }, [paymentsStatus]);

    useNonInitialEffect(() => {
        switch (walletsStatus) {
            case WalletsStatus.SaveSuccess:
                setShowAddBalance(false);
                setShowReduceBalance(false);
                dispatch(showSuccess('Balance updated successfully!'));
                break;
            case WalletsStatus.Error:
                dispatch(showError('Something went wrong. Please try again.'));
                break;
        }
    }, [walletsStatus]);

    const handleCardComplete = () => {
        dispatch(showSuccess('Credit card added successfully!'));
        setLoading(true);
        setShowAddCard(false);
        setTimeout(() => {
            dispatch(getClientPaymentMethodsThunk(apiClientWrapper, props.clientId));
        }, 4000);
    };

    const handleCardError = () => {
        dispatch(showError('Error adding credit card!'));
    };

    const handleDeleteCard = (card: ClientPaymentMethod) => {
        dispatch(deleteClientPaymentMethodThunk(apiClientWrapper, props.clientId, card.id));
        setShowDeleteCard(false);
        setCardSelectedToDelete(null);
    };

    const handleSetPrimary = (card: ClientPaymentMethod) => {
        setLoading(true);
        dispatch(setPrimaryClientPaymentMethodThunk(apiClientWrapper, props.clientId, card.id));
    };

    const handleAddBalance = (amount: number) => {
        if (!isOwner) {
            return;
        }

        dispatch(
            saveWalletAdjustmentThunk(apiClientWrapper, props.clientId, {
                type: WalletAdjustmentType.Add,
                amount
            })
        );
    };

    const handleReduceBalance = (amount: number) => {
        if (!isOwner) {
            return;
        }

        dispatch(
            saveWalletAdjustmentThunk(apiClientWrapper, props.clientId, {
                type: WalletAdjustmentType.Reduce,
                amount
            })
        );
    };

    return (
        <>
            <div className={styles.root}>
                {loading && (
                    <div className={styles.loadingContainer}>
                        <Spinner />
                    </div>
                )}

                {!loading && (
                    <>
                        {isMobile && (
                            <div className={styles.editWrapper}>
                                <ClientEditHeader
                                    client={props.clientRef}
                                    onSelect={props.onSelect}
                                    onBook={props.onBook}
                                    onNewSale={props.onNewSale}
                                    onClose={props.onClose}
                                />
                            </div>
                        )}

                        <Header onAdd={() => setShowAddCard(true)} />

                        <div className={styles.sectionsWrapper}>
                            {/* Card on File */}
                            <div className={styles.sectionRow}>
                                <div className={styles.sectionInfo}>
                                    <h2 className={styles.sectionTitle}>Card on File</h2>
                                    <p className={styles.sectionDescription}>
                                        Securely store a card to make future checkouts faster and easier.
                                    </p>
                                </div>
                                <div className={styles.sectionContent}>
                                    {cards.length > 0 ? (
                                        <CardGroup
                                            cards={cards}
                                            onError={handleCardError}
                                            hideDefaultBadge={true}
                                            onDeleteCard={card => {
                                                setCardSelectedToDelete(card);
                                                setShowDeleteCard(true);
                                            }}
                                            hideEditButton={true}
                                            clientId={props.clientId}
                                            showAddCard={showAddCard}
                                            onCloseAddCard={() => setShowAddCard(false)}
                                            onSetPrimary={handleSetPrimary}
                                            className={styles.cardGroup}
                                        />
                                    ) : (
                                        <div className={styles.emptyCardGroup}>
                                            <span className={styles.emptyCardText}>No card saved yet.</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Wallet Balance */}
                            <div className={styles.sectionRow}>
                                <div className={styles.sectionInfo}>
                                    <h2 className={styles.sectionTitle}>Wallet</h2>
                                    <p className={styles.sectionDescription}>
                                        Add funds to this client&apos;s wallet to be used toward future purchases at
                                        checkout.
                                    </p>
                                </div>
                                <div className={styles.sectionContent}>
                                    <p className={styles.balanceLabel}>Available Balance</p>
                                    <p className={styles.balanceAmount}>${balance.toFixed(2)}</p>
                                    {isOwner ? (
                                        <div className={styles.balanceActions}>
                                            <ButtonV3
                                                label="Add"
                                                leftIcon={faPlus}
                                                variant="primary"
                                                size="medium"
                                                onClick={() => setShowAddBalance(true)}
                                            />
                                            <div className={styles.reduceBalanceWrapper}>
                                                <ButtonV3
                                                    label="Reduce Balance"
                                                    variant="black"
                                                    size="medium"
                                                    onClick={() => setShowReduceBalance(true)}
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {/* Transaction History */}
                            <div className={styles.historySection}>
                                <h3 className={styles.historyTitle}>Transaction History</h3>
                                <WalletTable transactions={transactions} />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {showAddCard && (
                <AddCreditCard
                    clientId={props.clientId}
                    open={showAddCard}
                    onClose={() => setShowAddCard(false)}
                    onComplete={handleCardComplete}
                    onError={handleCardError}
                />
            )}

            {isOwner && showAddBalance && (
                <WalletAdjustmentModal
                    mode="add"
                    onClose={() => setShowAddBalance(false)}
                    onSave={handleAddBalance}
                    isSaving={isSaving}
                />
            )}

            {isOwner && showReduceBalance && (
                <WalletAdjustmentModal
                    mode="reduce"
                    availableBalance={balance}
                    onClose={() => setShowReduceBalance(false)}
                    onSave={handleReduceBalance}
                    isSaving={isSaving}
                />
            )}

            {showDeleteCard && cardSelectedToDelete && (
                <ConfirmDialog
                    open={showDeleteCard}
                    title={<Typography>Delete Card</Typography>}
                    onCancel={() => setShowDeleteCard(false)}
                    question={
                        <Typography style={{ maxWidth: 270, margin: '0px auto' }}>
                            Are you sure you want to delete the card •••• {cardSelectedToDelete.lastDigits}?
                        </Typography>
                    }
                    actions={
                        <div className={styles.dialogActions}>
                            <Button
                                label="No"
                                color="black"
                                disabled={loading}
                                id="wallet_dialog_delete_card_cancel"
                                onClick={() => setShowDeleteCard(false)}
                            />
                            <Button
                                label="Yes"
                                color="green"
                                loading={loading}
                                variant="danger"
                                id="wallet_dialog_delete_card_confirm"
                                onClick={() => handleDeleteCard(cardSelectedToDelete)}
                            />
                        </div>
                    }
                />
            )}
        </>
    );
};

export default ClientWallet;
